<template>
  <div class="min-h-screen bg-slate-50 px-4 py-5">
    <div class="mx-auto max-w-[1700px] space-y-4">
      <header class="flex flex-col gap-4 rounded-md border border-slate-200 bg-white px-4 py-3 shadow-sm lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-brand-700">Application Tracker</p>
          <h1 class="text-xl font-semibold text-slate-950">All Jobs</h1>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <base-button type="button" text="Add" @click="addNewJob"></base-button>
          <button
            @click="showInsights = true"
            class="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:border-brand-300 hover:text-brand-700"
          >
            <font-awesome-icon icon="chart-simple" />
            Insights
          </button>
          <button
            @click="refresh"
            class="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700"
            title="Refresh"
          >
            <font-awesome-icon icon="sync-alt" />
          </button>

          <div class="relative">
            <font-awesome-icon icon="search" class="absolute left-3 top-3 text-xs text-slate-400" />
            <input
              v-model="companySearch"
              placeholder="Search company or title"
              class="h-10 w-64 rounded-md border border-slate-300 bg-white pl-8 pr-8 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            <button
              v-if="companySearch"
              @click="resetCompanyFilter"
              class="absolute right-2 top-2 h-6 w-6 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              title="Clear search"
            >
              <font-awesome-icon icon="times" />
            </button>
          </div>

          <select
            v-model="selectedRange"
            class="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
            <option value="all">All time</option>
          </select>
        </div>
      </header>

      <section class="overflow-x-auto pb-2">
        <div class="min-w-[960px] rounded-md">
          <div class="grid grid-cols-4 gap-3">
            <div
              v-for="(column, index) in boardColumns"
              :key="`rail-${column.key}`"
              class="flex h-16 items-center justify-between rounded-md border border-t-2 border-slate-200 bg-white px-4 shadow-sm"
              :class="column.railClass"
            >
              <div class="min-w-0">
                <h2 class="truncate text-sm font-semibold text-slate-950">{{ column.label }}</h2>
                <p class="truncate text-xs text-slate-500">{{ column.shortDescription }}</p>
              </div>
              <div class="ml-3 flex shrink-0 items-center gap-3">
                <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{{ column.jobs.length }}</span>
                <font-awesome-icon
                  v-if="index < boardColumns.length - 1"
                  icon="chevron-right"
                  class="text-slate-300"
                />
              </div>
            </div>
          </div>

          <div class="mt-3 grid grid-cols-4 gap-3">
            <div v-for="column in boardColumns" :key="column.key" class="min-h-[660px] rounded-md border border-slate-200 bg-white shadow-sm">
              <div class="max-h-[760px] space-y-2 overflow-y-auto p-3">
                <article
                  v-for="job in column.jobs"
                  :key="`${column.key}-${job.id}`"
                  class="rounded-md border border-slate-200 bg-white p-3 transition hover:border-brand-200 hover:shadow-sm"
                >
                  <div
                    v-if="column.key !== 'applied' && job.latest_status"
                    class="mb-2 flex justify-end"
                  >
                    <span
                      class="inline-flex max-w-full rounded-full px-2 py-0.5 text-right text-[11px] font-medium leading-tight"
                      :class="statusBadgeClass(job.latest_status)"
                    >
                      {{ job.latest_status }}
                    </span>
                  </div>
                  <div>
                    <button
                      @click="openJobModal(job.id)"
                      class="w-full min-w-0 text-left text-sm font-semibold leading-snug text-slate-950 hover:text-brand-700"
                    >
                      {{ job.title }}
                    </button>
                  </div>

                  <p class="mt-1 truncate text-xs font-medium text-slate-600">{{ job.company }}</p>
                  <div
                    v-if="cardMetadata(job).length"
                    class="mt-1 flex min-w-0 flex-wrap items-center gap-y-1 text-[11px] text-slate-400"
                    aria-label="Job details"
                  >
                    <template v-for="(item, itemIndex) in cardMetadata(job)" :key="item.key">
                      <span v-if="itemIndex > 0" class="mx-2 text-slate-300" aria-hidden="true">|</span>
                      <span class="inline-flex min-w-0 items-center" :title="item.value">
                        <font-awesome-icon :icon="item.icon" class="mr-1 shrink-0 text-[10px] text-slate-400" />
                        <span class="max-w-[150px] truncate">{{ item.value }}</span>
                      </span>
                    </template>
                  </div>

                  <div class="mt-3 flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>{{ stageAgeLabel(job) }}</span>
                    <span v-if="skillMatchLabel(job)" class="shrink-0 rounded-full bg-slate-100 px-2 py-0.5">{{ skillMatchLabel(job) }}</span>
                  </div>

                  <div v-if="attentionLabel(job)" class="mt-2 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800">
                    {{ attentionLabel(job) }}
                  </div>
                  <button
                    v-if="shouldOfferClose(job)"
                    @click="closeStaleJob(job.id)"
                    class="mt-2 w-full rounded border border-amber-200 bg-white px-2 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
                  >
                    Close as not selected
                  </button>

                  <div class="mt-3 flex items-center justify-between gap-2">
                    <button
                      @click="toggleSkills(job.id)"
                      class="text-xs font-medium text-slate-400 hover:text-brand-700"
                    >
                      Skills
                    </button>
                    <div class="flex gap-1.5">
                      <a
                        v-if="job.job_url"
                        :href="job.job_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 hover:text-brand-600"
                        title="Open job posting"
                      >
                        <font-awesome-icon icon="external-link" />
                      </a>
                      <button
                        v-if="column.key === 'matched'"
                        @click="applyToFoundJob(job.id)"
                        class="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-xs text-slate-500 hover:bg-emerald-50 hover:text-emerald-700"
                        title="Mark as applied"
                      >
                        <font-awesome-icon icon="check" />
                      </button>
                      <button
                        v-else-if="!isClosed(job)"
                        @click="setInterview(job.id)"
                        class="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-xs text-slate-500 hover:bg-emerald-50 hover:text-emerald-700"
                        title="Add next step"
                      >
                        <font-awesome-icon icon="plus" />
                      </button>
                      <button
                        v-if="column.key === 'matched'"
                        @click="dismissFoundJob(job.id)"
                        class="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                        title="Dismiss"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                      <button
                        v-else-if="!isClosed(job)"
                        @click="rejectJob(job.id)"
                        class="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 text-xs text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                        title="Reject"
                      >
                        <font-awesome-icon icon="times" />
                      </button>
                    </div>
                  </div>

                  <div v-if="expandedSkills[job.id] && cardSkills(job).length > 0" class="mt-3 border-t border-slate-100 pt-2">
                    <div class="flex flex-wrap gap-1">
                      <span
                        v-for="skill in cardSkills(job)"
                        :key="skill.skill_name || skill.id"
                        :class="[
                          'rounded-full px-2 py-0.5 text-xs',
                          skill.user_has_skill ? 'bg-brand-50 text-brand-700' : 'bg-slate-50 text-slate-500',
                        ]"
                      >
                        {{ skill.skill_name }}
                      </span>
                    </div>
                  </div>
                </article>

                <p v-if="column.jobs.length === 0" class="py-8 text-center text-sm text-slate-400">{{ column.empty }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-if="showInsights" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4" @click="showInsights = false">
      <div class="w-full max-w-2xl rounded-md bg-white shadow-xl" @click.stop>
        <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 class="text-lg font-semibold text-slate-950">Pipeline Insights</h2>
            <p class="text-xs text-slate-500">Based on the current filters.</p>
          </div>
          <button class="text-slate-400 hover:text-slate-700" @click="showInsights = false">
            <font-awesome-icon icon="times" />
          </button>
        </div>
        <div class="p-5">
          <table class="w-full border-collapse text-sm">
            <tbody>
              <tr v-for="row in insightRows" :key="row.label" class="border-b border-slate-100 last:border-b-0">
                <th class="py-2.5 pr-4 text-left font-medium text-slate-600">{{ row.label }}</th>
                <td class="py-2.5 text-right font-semibold text-slate-950">{{ row.value }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <DatePickerModal :is-open="isDatePickerOpen" @close="closeDatePicker" :statuses="statuses" @dateSelected="handleDateSelected" />

    <JobDetailModal
      v-if="selectedJob"
      :job="selectedJob"
      :is-open="isJobModalOpen"
      :user-skills="skills"
      :statuses="statuses"
      @close="closeJobModal"
      @status-updated="onJobStatusUpdated"
      @skill-added="onJobSkillAdded"
    />

    <SkillsSetupModal v-if="showSkillsModal" />
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, reactive } from "vue";
import { useRouter } from "vue-router";
import api, { fetchUserSkills } from "@/services/api";
import BaseButton from "@/components/BaseButton.vue";
import SkillsSetupModal from "@/components/SkillsSetupModal.vue";
import DatePickerModal from "@/components/DateModal.vue";
import JobDetailModal from "@/components/JobDetailModal.vue";
import { toLocalDateInputValue } from "@/utils/date";

const INTERVIEW_STATUSES = [
  "Phone Screen",
  "Recruiter Screen",
  "Hiring Manager Screen",
  "Interview",
  "First Interview",
  "Second Interview",
  "Hiring Manager Interview",
  "Team Interview",
  "Panel Interview",
  "Final Interview",
  "Technical Interview",
  "Technical Assessment",
  "Case Study",
  "Portfolio Review",
  "Assessment",
];
const OFFER_STATUSES = ["Offer Received", "Reference Check", "Background Check", "Negotiation"];
const CLOSED_STATUSES = ["Accepted", "Rejected", "Not selected", "Withdrawn"];
const ACTIVE_STATUSES = ["Applied", ...INTERVIEW_STATUSES, ...OFFER_STATUSES];
const dayMs = 1000 * 60 * 60 * 24;

interface CardMetadataItem {
  key: string;
  icon: string;
  value: string;
}

export default {
  components: {
    BaseButton,
    DatePickerModal,
    JobDetailModal,
    SkillsSetupModal,
  },
  setup() {
    const router = useRouter();
    const skills = ref<any[]>([]);
    const jobs = ref<any[]>([]);
    const foundJobs = ref<any[]>([]);
    const statuses = ref<string[]>([]);
    const selectedRange = ref("90");
    const companySearch = ref("");
    const showInsights = ref(false);
    const isDatePickerOpen = ref(false);
    const currentJobId = ref<number | null>(null);
    const selectedJob = ref<any | null>(null);
    const isJobModalOpen = ref(false);
    const expandedSkills = reactive({} as Record<number, boolean>);
    const showSkillsModal = ref<boolean>(false);

    const today = () => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return now;
    };

    const parseDate = (dateString?: string) => {
      if (!dateString) return null;
      const [year, month, day] = dateString.split("-").map(Number);
      if (!year || !month || !day) return null;
      const parsed = new Date(year, month - 1, day);
      parsed.setHours(0, 0, 0, 0);
      return parsed;
    };

    const diffDays = (dateString?: string) => {
      const date = parseDate(dateString);
      if (!date) return 0;
      return Math.max(0, Math.floor((today().getTime() - date.getTime()) / dayMs));
    };

    const formatCount = (count: number) => new Intl.NumberFormat().format(count);
    const percentage = (part: number, total: number) => (!total ? "0%" : `${Math.round((part / total) * 100)}%`);
    const hasStep = (job: any, stageStatuses: string[]) => (job.process_steps || []).some((step: any) => stageStatuses.includes(step.process_type));
    const isClosed = (job: any) => CLOSED_STATUSES.includes(job.latest_status);

    const dateInRange = (job: any) => {
      if (selectedRange.value === "all") return true;
      const date = parseDate(job.application_date);
      if (!date) return true;
      const cutoff = today();
      cutoff.setDate(cutoff.getDate() - Number(selectedRange.value));
      return date >= cutoff;
    };

    const matchesText = (job: any) => {
      const searchTerm = companySearch.value.trim().toLowerCase();
      if (!searchTerm) return true;
      return [job.company, job.title, job.location]
        .filter(Boolean)
        .some((value: string) => value.toLowerCase().includes(searchTerm));
    };

    const filteredTrackedJobs = computed(() => {
      return jobs.value.filter((job) => {
        if (!matchesText(job)) return false;
        if (!dateInRange(job)) return false;
        return true;
      });
    });

    const filteredFoundJobs = computed(() => foundJobs.value.filter((job) => matchesText(job)));
    const appliedJobs = computed(() => filteredTrackedJobs.value.filter((job) => job.latest_status === "Applied"));
    const interviewJobs = computed(() => filteredTrackedJobs.value.filter((job) => INTERVIEW_STATUSES.includes(job.latest_status)));
    const offerJobs = computed(() => filteredTrackedJobs.value.filter((job) => OFFER_STATUSES.includes(job.latest_status)));
    const closedJobs = computed(() => jobs.value.filter((job) => isClosed(job) && matchesText(job) && dateInRange(job)));

    const boardColumns = computed(() => [
      {
        key: "matched",
        label: "Matched",
        shortDescription: "Review",
        jobs: filteredFoundJobs.value,
        empty: "No matched jobs.",
        railClass: "border-t-brand-500",
      },
      {
        key: "applied",
        label: "Applied",
        shortDescription: "Waiting",
        jobs: appliedJobs.value,
        empty: "No applications.",
        railClass: "border-t-slate-300",
      },
      {
        key: "interview",
        label: "Interview",
        shortDescription: "In process",
        jobs: interviewJobs.value,
        empty: "No interviews.",
        railClass: "border-t-violet-400",
      },
      {
        key: "offer",
        label: "Offer",
        shortDescription: "Decision stage",
        jobs: offerJobs.value,
        empty: "No offers.",
        railClass: "border-t-emerald-500",
      },
    ]);

    const reachedApplied = computed(() => filteredTrackedJobs.value.filter((job) => hasStep(job, ["Applied"]) || ACTIVE_STATUSES.includes(job.latest_status) || isClosed(job)).length);
    const reachedInterview = computed(() => filteredTrackedJobs.value.filter((job) => hasStep(job, INTERVIEW_STATUSES)).length);
    const reachedOffer = computed(() => filteredTrackedJobs.value.filter((job) => hasStep(job, OFFER_STATUSES)).length);
    const reachedAccepted = computed(() => filteredTrackedJobs.value.filter((job) => hasStep(job, ["Accepted"]) || job.latest_status === "Accepted").length);

    const averageDaysToStep = (stageStatuses: string[]) => {
      const durations = filteredTrackedJobs.value
        .map((job) => {
          const applied = parseDate(job.application_date);
          const target = (job.process_steps || [])
            .filter((step: any) => stageStatuses.includes(step.process_type))
            .map((step: any) => parseDate(step.process_date))
            .filter(Boolean)
            .sort((a: Date, b: Date) => a.getTime() - b.getTime())[0];
          if (!applied || !target) return null;
          return Math.max(0, Math.round((target.getTime() - applied.getTime()) / dayMs));
        })
        .filter((duration: number | null) => duration !== null) as number[];
      if (!durations.length) return "n/a";
      return `${Math.round(durations.reduce((sum, days) => sum + days, 0) / durations.length)}d`;
    };

    const insightRows = computed(() => [
      { label: "Matched jobs", value: formatCount(filteredFoundJobs.value.length) },
      { label: "Tracked applications", value: formatCount(filteredTrackedJobs.value.length) },
      { label: "Applied", value: formatCount(reachedApplied.value) },
      { label: "Reached interview", value: formatCount(reachedInterview.value) },
      { label: "Offers", value: formatCount(reachedOffer.value) },
      { label: "Accepted", value: formatCount(reachedAccepted.value) },
      { label: "Closed applications", value: formatCount(closedJobs.value.length) },
      { label: "Apply to interview rate", value: percentage(reachedInterview.value, reachedApplied.value) },
      { label: "Interview to offer rate", value: percentage(reachedOffer.value, reachedInterview.value) },
      { label: "Average time to interview", value: averageDaysToStep(INTERVIEW_STATUSES) },
      { label: "Average time to offer", value: averageDaysToStep(OFFER_STATUSES) },
      {
        label: "Rejected after interview",
        value: formatCount(jobs.value.filter((job) => job.latest_status === "Rejected" && hasStep(job, INTERVIEW_STATUSES)).length),
      },
    ]);

    const daysSince = (dateString?: string) => {
      const days = diffDays(dateString);
      if (days === 0) return "Today";
      if (days === 1) return "1 day";
      return `${days} days`;
    };

    const daysInCurrentStage = (job: any) => daysSince(job.latest_status_date || job.application_date);
    const stageAgeLabel = (job: any) => (job.latest_status ? `${daysInCurrentStage(job)} in stage` : "No status");

    const attentionLabel = (job: any) => {
      if (isClosed(job) || OFFER_STATUSES.includes(job.latest_status)) return "";
      const days = diffDays(job.latest_status_date || job.application_date);
      if (job.latest_status === "Applied" && days >= 30) return `No interview after ${days} days`;
      if (job.latest_status === "Applied" && days >= 14) return `No response for ${days} days`;
      if (INTERVIEW_STATUSES.includes(job.latest_status) && days >= 7) return `No movement for ${days} days`;
      return "";
    };

    const shouldOfferClose = (job: any) => {
      return job.latest_status === "Applied" && diffDays(job.latest_status_date || job.application_date) >= 30;
    };

    const statusBadgeClass = (status: string) => {
      if (status === "Applied") return "bg-slate-100 text-slate-700";
      if (INTERVIEW_STATUSES.includes(status)) return "bg-violet-50 text-violet-700";
      if (OFFER_STATUSES.includes(status)) return "bg-emerald-50 text-emerald-700";
      if (status === "Accepted") return "bg-green-50 text-green-700";
      if (["Rejected", "Not selected", "Withdrawn"].includes(status)) return "bg-rose-50 text-rose-700";
      return "bg-slate-100 text-slate-700";
    };

    const cardMetadata = (job: any): CardMetadataItem[] => {
      const workTypeLabels: Record<string, string> = {
        "0": "On-site",
        "1": "Remote",
        "2": "Hybrid",
      };
      const rawWorkType = job.work_type;
      const workType = job.is_contract
        ? "Contract"
        : rawWorkType === null || rawWorkType === undefined
          ? ""
          : workTypeLabels[String(rawWorkType)] || String(rawWorkType).trim();
      const salary = job.salary_range || job.salary;

      return [
        job.location
          ? { key: "location", icon: "location-dot", value: String(job.location) }
          : null,
        workType
          ? { key: "work-type", icon: "clock", value: workType }
          : null,
        salary
          ? { key: "salary", icon: "money-bill-wave", value: String(salary) }
          : null,
      ].filter((item): item is CardMetadataItem => item !== null);
    };

    const cardSkills = (job: any) => Array.isArray(job.skills) ? job.skills : [];

    const skillMatchLabel = (job: any) => {
      const currentSkills = cardSkills(job);
      if (!currentSkills.length) return "";
      const matched = currentSkills.filter((skill: any) => skill.user_has_skill).length;
      return `${matched}/${currentSkills.length} skills`;
    };

    const openJobModal = async (jobId: number) => {
      try {
        const response = await api.fetchJobDetails(jobId);
        selectedJob.value = response.data;
        isJobModalOpen.value = true;
      } catch (e) {
        console.error("Error fetching job details:", e);
      }
    };

    const closeJobModal = () => {
      isJobModalOpen.value = false;
      setTimeout(() => {
        selectedJob.value = null;
      }, 300);
    };

    const onJobStatusUpdated = async () => {
      await fetchJobs();
      await fetchFoundJobsList();
    };

    const onJobSkillAdded = async () => {
      const selectedJobId = selectedJob.value?.id;
      try {
        await Promise.all([fetchJobs(), fetchFoundJobsList(), fetchSkills()]);
        if (
          !selectedJobId
          || !isJobModalOpen.value
          || selectedJob.value?.id !== selectedJobId
        ) return;

        const response = await api.fetchJobDetails(selectedJobId);
        if (isJobModalOpen.value && selectedJob.value?.id === selectedJobId) {
          Object.assign(selectedJob.value, response.data);
        }
      } catch {
        // The skill mutation already succeeded; retain the modal's optimistic state.
      }
    };

    const setInterview = (jobId: number) => {
      currentJobId.value = jobId;
      isDatePickerOpen.value = true;
    };

    const closeDatePicker = () => {
      isDatePickerOpen.value = false;
    };

    const handleDateSelected = async (date: string, status: string, time?: string, note?: string) => {
      if (!currentJobId.value || !status) {
        closeDatePicker();
        return;
      }
      const selectedDate = date || toLocalDateInputValue();
      await api.updateJobStatus(currentJobId.value, status, selectedDate, time, note);
      await fetchJobs();
      await fetchFoundJobsList();
      currentJobId.value = null;
      closeDatePicker();
    };

    const fetchFoundJobsList = async () => {
      try {
        const result = await api.fetchFoundJobs();
        foundJobs.value = result.data.jobs || [];
      } catch (e) {
        console.error("Error fetching found jobs:", e);
      }
    };

    const applyToFoundJob = async (jobId: number) => {
      const selectedDate = toLocalDateInputValue();
      await api.updateJobStatus(jobId, "Applied", selectedDate);
      await fetchJobs();
      await fetchFoundJobsList();
    };

    const dismissFoundJob = async (jobId: number) => {
      const selectedDate = toLocalDateInputValue();
      await api.updateJobStatus(jobId, "Dismissed", selectedDate);
      await fetchFoundJobsList();
    };

    const rejectJob = async (jobId: number) => {
      const selectedDate = toLocalDateInputValue();
      await api.updateJobStatus(jobId, "Rejected", selectedDate);
      await fetchJobs();
      await fetchFoundJobsList();
    };

    const closeStaleJob = async (jobId: number) => {
      const selectedDate = toLocalDateInputValue();
      await api.updateJobStatus(jobId, "Not selected", selectedDate);
      await fetchJobs();
      await fetchFoundJobsList();
    };

    const toggleSkills = (jobId: number) => {
      expandedSkills[jobId] = !expandedSkills[jobId];
    };

    const resetCompanyFilter = () => {
      companySearch.value = "";
    };

    const fetchJobs = async () => {
      const response = await api.fetchJobs();
      jobs.value = response.data.jobs || [];
    };

    const fetchSkills = async () => {
      const response = await api.fetchUserSkills();
      skills.value = response.data as any[];
    };

    const fetchStatuses = async () => {
      const response = await api.fetchStatuses();
      statuses.value = response.data.statuses;
    };

    const refresh = async () => {
      await Promise.all([fetchJobs(), fetchStatuses(), fetchFoundJobsList()]);
    };

    const addNewJob = () => {
      router.push("/add");
    };

    onMounted(async () => {
      await Promise.all([fetchJobs(), fetchStatuses(), fetchSkills(), fetchFoundJobsList()]);
      const skillsRes = await fetchUserSkills();
      if ((skillsRes.data as any[]).length === 0) showSkillsModal.value = true;
    });

    return {
      jobs,
      statuses,
      foundJobs,
      skills,
      selectedRange,
      companySearch,
      showInsights,
      insightRows,
      boardColumns,
      selectedJob,
      isJobModalOpen,
      isDatePickerOpen,
      expandedSkills,
      showSkillsModal,
      addNewJob,
      refresh,
      resetCompanyFilter,
      openJobModal,
      closeJobModal,
      onJobStatusUpdated,
      onJobSkillAdded,
      setInterview,
      closeDatePicker,
      handleDateSelected,
      applyToFoundJob,
      dismissFoundJob,
      rejectJob,
      closeStaleJob,
      toggleSkills,
      statusBadgeClass,
      stageAgeLabel,
      attentionLabel,
      shouldOfferClose,
      cardSkills,
      cardMetadata,
      skillMatchLabel,
      isClosed,
    };
  },
};
</script>
